import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export function useParties() {
  return useQuery({ queryKey: ["parties"], queryFn: async () => (await api.get("/parties")).data });
}

// The built-in payer's name: the 529 account money comes out of (ZEN-819).
// The server has its own copy, helpers.ACCOUNT_529_NAME; keep the two equal.
export const ACCOUNT_529_NAME = "529 account";

// Resolve a party ref to its nickname.
export function nickFor(ref, parties, studentNick) {
  if (!ref) return "";
  if (ref === "student") return studentNick || "Student";
  if (ref === "529") return ACCOUNT_529_NAME;
  if (ref === "owner") return parties?.owner?.nickname || "Owner";
  if (ref.startsWith("party:")) {
    const id = ref.slice(6);
    const p = (parties?.custom || []).find((x) => x.id === id);
    return p ? p.nickname : "Party";
  }
  return ref;
}

// Build the precise routing label for an expense.
// Format: "[Eligibility] · [Flow]" — eligibility token always leads.
export function expenseRoutingLabel(e, parties, studentNick) {
  // An expense nobody has classified yet has no eligibility to report. It is
  // STORED with the boolean false, because the importer collapses "undecided"
  // onto it — so every branch below would confidently print "Not 529-eligible"
  // for a decision the owner has never made. Saying so on a row is the same
  // fabrication the editor was fixed for; the row must ask, not answer.
  // (ZEN-113)
  if (e.status === "draft") return "Waiting for your 529 decision";
  const st = e.settlement;
  const pr = e.payor_ref;
  const src = e.source;
  const payorNick = nickFor(pr, parties, studentNick);
  if (src === "529") {
    if (pr === "529") return "529-eligible · Direct-paid (to institution)";
    if (pr === "student") return "529-eligible · Student-paid";
    return `529-eligible · ${payorNick} fronted → ${studentNick} reimburses ${payorNick}`;
  }
  const elig = e.eligible_529 ? "529-eligible" : "Not 529-eligible";
  if (src === "private") {
    const rNick = nickFor(st?.reimburser_ref, parties, studentNick);
    return `${elig} · Reimbursed by ${rNick} (outside 529)`;
  }
  // none — not reimbursed
  if (pr === "student") return `${elig} · Student-paid — not reimbursed`;
  return `${elig} · Covered by ${payorNick} — not reimbursed`;
}
